import React, { useEffect, useState, useCallback, useRef, useMemo } from "react"
import * as XLSX from "xlsx"
import { useLocation, useNavigate, Link, useParams } from "react-router-dom"
import KeywordTrackingDetailsModal from "../../components/Common/Modal/KeywordTrackingDetailsModal"
import AppsTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import UnsavedChangesModal from "../../components/Common/Modal/UnsavedChangesModal"
// Custom hooks and utilities
import { useKeywordTrackingAPI } from "./hooks/useKeywordTrackingAPI"
import { getCountryName, getCountryCode, getCountryFlag } from "./utils"
import flags from "../../common/flag"
import FlagSelectDropdown, { KEYWORD_TRACKING_THEME } from "../../components/Common/FlagSelectDropdown"

// Components
import AppHeader from "./components/AppHeader"
import FilterControls from "./components/FilterControls"
import KeywordSearch from "./components/KeywordSearch"
import SuggestedGenresModal from "../../components/Common/Modal/SuggestedGenresModal"
import KeywordTrackingTable from "./components/KeywordTrackingTable"
import DateRangeSelector from "./components/DateRangeSelector"
import SkeletonCard from "./components/SkeletonCard"
// Same fix as KeywordTracking's keywordDetails.jsx — a local react-toastify
// ToastContainer/toast rendered plain top-right popups, inconsistent with
// the rest of the app. Switched to the shared toastr-based helper (dark/
// pink-branded, bottom-right, stacks cleanly, manages its own container).
import { triggerToast } from "../../utils/toast"
import { api } from "../../utils/variables"
import axios from "axios"
import { BUTTON_STYLES, COLORS } from "./constants"
import { Button, Modal, ModalBody } from "reactstrap"
import SEO from "../../components/Common/SEO"
import { generateSlug } from "../../utils/seoUtils"
import NotFound from "../../components/Common/NotFound"
import userProfileApi from "../../services/userProfileApi"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

// Add a constant for storing countries data in localStorage
const COUNTRIES_DATA_KEY = "keyword_tracking_countries_data"

// Function to get stored countries data
const getStoredCountriesData = () => {
    const data = localStorage.getItem(COUNTRIES_DATA_KEY)
    return data ? JSON.parse(data) : []
}

// Function to store countries data
const storeCountriesData = data => {
    if (data && data.length > 0) {
        localStorage.setItem(COUNTRIES_DATA_KEY, JSON.stringify(data))
    }
}

// Storage keys
const getStorageKey = (itemId, key) => `category_tracking_${itemId}_${key}`
const getStoredValue = (itemId, key, defaultValue) => {
    const value = localStorage.getItem(getStorageKey(itemId, key))
    return value ? JSON.parse(value) : defaultValue
}
const storeValue = (itemId, key, value) => {
    if (itemId) {
        localStorage.setItem(getStorageKey(itemId, key), JSON.stringify(value))
    }
}

// Simple debounce function
const debounce = (func, delay) => {
    let timeoutId
    return (...args) => {
        if (timeoutId) {
            clearTimeout(timeoutId)
        }
        timeoutId = setTimeout(() => {
            func(...args)
        }, delay)
    }
}
const ExportModal = ({ show, handleClose, gameId, dataType }) => {
    const { trackEvent } = useAnalytics()
    const formatDateToYYYYMMDD = date => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const day = String(date.getDate()).padStart(2, "0")
        return `${year}-${month}-${day}`
    }

    const [fromDate, setFromDate] = useState(formatDateToYYYYMMDD(new Date()))
    const [toDate, setToDate] = useState(formatDateToYYYYMMDD(new Date()))
    const [loading, setLoading] = useState(false)

    const getCsv = async () => {
        const data = {
            game: gameId, // <- pass down from props
            type: "keyword", // <- or make this dynamic
            start_date: fromDate,
            end_date: toDate,
        }

        try {
            setLoading(true)
            const res = await axios.post(`${api}/analytics/generate_csv`, data, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            })

            if (res.data?.link) {
                const link = document.createElement("a")
                link.href = res.data.link
                link.setAttribute("download", "")
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)

                trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_EXPORTED, {
                    from_date: fromDate,
                    to_date: toDate,
                })

                triggerToast("Export successful!", "success")
                handleClose()
            } else {
                triggerToast("No CSV link returned from server.", "error")
            }
        } catch (err) {
            console.error("Export failed", err)
            triggerToast("Export failed. Please try again.", "error")
        } finally {
            setLoading(false)
        }
    }

    if (!show) return null

    return (
        <div className="modal-overlay" style={modalOverlayStyle}>
            <div className="modal-container" style={modalContainerStyle}>
                <div className="modal-header" style={modalHeaderStyle}>
                    <h3
                        style={{
                            margin: 0,
                            fontSize: "18px",
                            fontWeight: 600,
                            color: "white",
                        }}
                    >
                        Select Date Range
                    </h3>
                    <button onClick={handleClose} style={closeButtonStyle}>
                        ×
                    </button>
                </div>
                <div className="modal-body" style={{ padding: "24px" }}>
                    <div
                        className="date-inputs"
                        style={{ display: "flex", gap: "20px", marginBottom: "32px" }}
                    >
                        <div className="date-group" style={{ flex: 1 }}>
                            <label style={labelStyle}>From</label>
                            <input
                                type="date"
                                value={fromDate}
                                max={formatDateToYYYYMMDD(new Date())}
                                onChange={e => setFromDate(e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <div className="date-group" style={{ flex: 1 }}>
                            <label style={labelStyle}>To</label>
                            <input
                                type="date"
                                value={toDate}
                                max={formatDateToYYYYMMDD(new Date())}
                                onChange={e => setToDate(e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                    </div>
                    <button
                        onClick={getCsv}
                        disabled={loading}
                        style={{
                            ...exportButtonStyle,
                            opacity: loading ? 0.7 : 1,
                            cursor: loading ? "not-allowed" : "pointer",
                        }}
                    >
                        {loading ? "Exporting..." : "Export"}
                    </button>
                </div>
            </div>
        </div>
    )
}
const modalOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
}
const modalContainerStyle = {
    background: "#2a2d3a",
    borderRadius: 8,
    width: 400,
    maxWidth: "90vw",
    color: "white",
}
const modalHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    borderBottom: "1px solid #3a3d4a",
}
const closeButtonStyle = {
    background: "none",
    border: "none",
    color: "#888",
    fontSize: 24,
    cursor: "pointer",
    padding: 0,
    lineHeight: 1,
}
const labelStyle = {
    display: "block",
    color: "#b0b3b8",
    fontSize: 14,
    marginBottom: 8,
    fontWeight: 500,
}
const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    background: "#1e2028",
    border: "1px solid #3a3d4a",
    borderRadius: 6,
    color: "white",
    fontSize: 14,
    outline: "none",
}
const exportButtonStyle = {
    width: "100%",
    background: "linear-gradient(135deg, #F6255A 0%, #F6255A 100%)",
    color: "white",
    border: "none",
    borderRadius: 6,
    padding: "14px 24px",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
}

const KeywordDetails = () => {
    const { trackEvent } = useAnalytics()
    const location = useLocation()
    const navigate = useNavigate()
    const { appName } = useParams()
    const queryParams = new URLSearchParams(location.search)
    const item_id =
        queryParams.get("item_id") ||
        queryParams.get("id") ||
        location.state?.item_id ||
        localStorage.getItem("tracked_item_id")
    const [showExportModal, setShowExportModal] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
    const [showSuggestedGenresModal, setShowSuggestedGenresModal] = useState(false)
    const [pendingNavigation, setPendingNavigation] = useState(null)
    const [isNavigating, setIsNavigating] = useState(false)
    // No stored selection yet (e.g. a fresh browser/session for this account)
    // must fall back to "" rather than a hardcoded "United States"/"english" —
    // several effects below (notably the auto-select-first-tracked-pair effect)
    // gate on `!selectedCountry` to detect "nothing selected yet", and a truthy
    // hardcoded default defeated that check, pinning every fresh session to
    // US/English instead of the account's real tracked country/language and
    // leaving its actual keyword data (and count) unfetched.
    const [selectedCountry, setSelectedCountry] = useState(() =>
        getStoredValue(item_id, "selected_country", "")
    )
    const [availableLanguages, setAvailableLanguages] = useState([])
    const [selectedLanguage, setSelectedLanguage] = useState(() =>
        getStoredValue(item_id, "selected_language", "")
    )
    const [activeKeyword, setActiveKeyword] = useState(() =>
        getStoredValue(item_id, "active_keyword", null)
    )
    const [searchInput, setSearchInput] = useState("")
    const [pendingKeywords, setPendingKeywords] = useState(new Set())
    const [pendingPairs, setPendingPairs] = useState(new Set())
    const [isTrackingInProgress, setIsTrackingInProgress] = useState(false)
    const [trackedKeywords, setTrackedKeywords] = useState(() =>
        getStoredValue(item_id, "tracked_keywords", [])
    )
    const [trackedCountries, setTrackedCountries] = useState(() =>
        getStoredValue(item_id, "tracked_countries", [])
    )
    const [trackedCountryLanguagePairs, setTrackedCountryLanguagePairs] =
        useState(() =>
            getStoredValue(item_id, "tracked_country_language_pairs", [])
        )
    const [activeTrackedCountry, setActiveTrackedCountry] = useState(() =>
        getStoredValue(item_id, "active_tracked_country", "")
    )
    const [activeTrackedPair, setActiveTrackedPair] = useState(() =>
        getStoredValue(item_id, "active_tracked_pair", "")
    )
    const [selectionError, setSelectionError] = useState(false)
    const [fromDate, setFromDate] = useState(new Date("2024-08-08"))
    const [toDate, setToDate] = useState(new Date())
    const [showAllKeywords, setShowAllKeywords] = useState(false)
    const [initialFetchDone, setInitialFetchDone] = useState(false) // Start with no fetch done

    // Add a state to track keywords specific to each country-language pair
    const [keywordsByCountryLanguage, setKeywordsByCountryLanguage] = useState(
        () => getStoredValue(item_id, "keywords_by_country_language", {})
    )
    // On a fresh browser/session there is no localStorage cache, so the state
    // above starts as `{}` — indistinguishable from "account genuinely tracks
    // 0 genres" until the mount-time fetch below resolves. Anything that reads
    // keywordsByCountryLanguage to show a count (e.g. the AI Genre
    // Recommendations modal's "slots remaining") should wait for this flag
    // rather than trusting an empty object at first render.
    const [categoryTrackingDataLoaded, setCategoryTrackingDataLoaded] = useState(false)

    // Initialize countriesData from localStorage if available
    const [countriesDataLoaded, setCountriesDataLoaded] = useState(false)

    const [windowWidth, setWindowWidth] = useState(() => window.innerWidth)
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const hasUnsavedChanges = useCallback(() => {
        return false
    }, [])

    // Navigation blocking handler
    const handleNavigationAttempt = useCallback((targetPath) => {
        if (hasUnsavedChanges()) {
            setPendingNavigation(targetPath)
            setShowUnsavedChangesModal(true)
            return true // Block navigation
        }
        return false // Allow navigation
    }, [hasUnsavedChanges])

    // Confirm leaving without saving
    const confirmLeaveWithoutSaving = useCallback(() => {
        console.log('confirmLeaveWithoutSaving called, pendingNavigation:', pendingNavigation)
        setIsNavigating(true)
        setShowUnsavedChangesModal(false)

        // Remove beforeunload listener to prevent double confirmation
        if (beforeUnloadRef.current) {
            window.removeEventListener('beforeunload', beforeUnloadRef.current)
            beforeUnloadRef.current = null
        }

        // Small delay to show loading state
        setTimeout(() => {
            if (pendingNavigation) {
                // Use window.location.href for more reliable navigation
                window.location.href = pendingNavigation
            } else {
                // If no pending navigation, go to dashboard as default
                navigate('/dashboard')
            }
        }, 500)
    }, [pendingNavigation, navigate])

    // Cancel leaving
    const cancelLeaveWithoutSaving = useCallback(() => {
        setShowUnsavedChangesModal(false)
        setPendingNavigation(null)
        setIsNavigating(false)
    }, [])

    // Override navigate function to check for unsaved changes
    const safeNavigate = useCallback((to, options = {}) => {
        if (!handleNavigationAttempt(to)) {
            navigate(to, options)
        }
    }, [navigate, handleNavigationAttempt])

    // Add beforeunload event listener for browser back/refresh
    const beforeUnloadRef = useRef(null)
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges()) {
                e.preventDefault()
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
                return e.returnValue
            }
        }

        beforeUnloadRef.current = handleBeforeUnload

        // Intercept link clicks - simplified version
        const handleLinkClick = (e) => {
            const target = e.target.closest('a')
            if (target && hasUnsavedChanges()) {
                const href = target.getAttribute('href')
                if (href && (href.startsWith('/') || href.includes(window.location.hostname))) {
                    e.preventDefault()
                    e.stopPropagation()
                    setPendingNavigation(href)
                    setShowUnsavedChangesModal(true)
                    return false
                }
            }
            return true
        }

        // Listen for browser navigation
        const handlePopState = (e) => {
            if (hasUnsavedChanges()) {
                e.preventDefault()
                e.stopPropagation()
                window.history.pushState(null, null, window.location.pathname)
                setShowUnsavedChangesModal(true)
                return false
            }
        }

        window.addEventListener('beforeunload', handleBeforeUnload)
        document.addEventListener('click', handleLinkClick, true)
        window.addEventListener('popstate', handlePopState)

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
            document.removeEventListener('click', handleLinkClick, true)
            window.removeEventListener('popstate', handlePopState)
            beforeUnloadRef.current = null
        }
    }, [hasUnsavedChanges])
    // Memoize modal results to avoid re-renders

    // On mount (or when item_id becomes available), ensure stored tracked lists are deduplicated
    useEffect(() => {
        if (!item_id) return

        // Dedupe tracked country-language pairs
        const dedupPairs = Array.from(new Set(trackedCountryLanguagePairs))
        if (dedupPairs.length !== trackedCountryLanguagePairs.length) {
            setTrackedCountryLanguagePairs(dedupPairs)
            console.log("LOGGINGGGG HEREEEE 430")
            storeValue(item_id, "tracked_country_language_pairs", dedupPairs)
        }

        // Dedupe tracked countries
        const dedupCountries = Array.from(new Set(trackedCountries))
        if (dedupCountries.length !== trackedCountries.length) {
            setTrackedCountries(dedupCountries)
            storeValue(item_id, "tracked_countries", dedupCountries)
        }
    }, [item_id])

    // Helper function to get the current pair key
    const getCurrentPairKey = () => {
        if (selectedCountry && selectedLanguage) {
            return `${selectedCountry}_${selectedLanguage}`
        }
        return null
    }

    const getActivePairKey = () => {
        if (activeTrackedPair) {
            return activeTrackedPair.replace(' (', '_').replace(')', '')
        }
        return getCurrentPairKey()
    }

    // Helper function to get keywords for the current country-language pair
    const getKeywordsForCurrentPair = () => {
        const pairKey = getCurrentPairKey()

        if (pairKey && keywordsByCountryLanguage[pairKey]) {
            const keywords = keywordsByCountryLanguage[pairKey].filter(
                keyword => keyword !== "game_data"
            )
            return keywords
        }
        return []
    }

    // Keywords for the chip display — follows activeTrackedPair tab, not the dropdown
    const getKeywordsForActivePair = () => {
        const pairKey = getActivePairKey()
        if (pairKey && keywordsByCountryLanguage[pairKey]) {
            return keywordsByCountryLanguage[pairKey].filter(k => k !== "game_data")
        }
        return []
    }
    // Upgrade modal state
    const [showUpgradeModal, setShowUpgradeModal] = useState(false)
    const [keywordToDelete, setKeywordToDelete] = useState(null)
    const [pairToDelete, setPairToDelete] = useState(null)
    const [currentPlan, setCurrentPlan] = useState(null)
    const [planLoading, setPlanLoading] = useState(false)

    // Upgrade error detection functions
    // Fetch plan on mount to know the genre limit
    useEffect(() => {
        const fetchPlanData = async () => {
            try {
                setPlanLoading(true)
                const [currentPlanResponse, productsResponse] = await Promise.all([
                    userProfileApi.fetchCurrentPlan(),
                    userProfileApi.fetchPlans()
                ])
                if (currentPlanResponse?.data?.product && productsResponse?.data) {
                    const currentProduct = productsResponse.data.find(
                        product => product.id === currentPlanResponse.data.product
                    )
                    if (currentProduct) {
                        setCurrentPlan({ ...currentPlanResponse.data, productDetails: currentProduct })
                    }
                }
            } catch (err) {
                console.error("Failed to fetch plan data:", err)
            } finally {
                setPlanLoading(false)
            }
        }
        fetchPlanData()
    }, [])

    const getKeywordLimit = () => {
        if (!currentPlan || !currentPlan.productDetails) return '5'
        const benefits = currentPlan.productDetails.description || []
        const benefit = benefits.find(b => {
            if (!b.benefit_name) return false
            const name = b.benefit_name.toLowerCase()
            if (name.includes('explorer') || name.includes('searches')) return false
            return name === 'keyword & category rank' ||
                name.includes('keyword & category rank') ||
                (name.includes('keyword') && name.includes('rank'))
        })
        if (benefit) {
            const patterns = [
                /upto\s*(\d+)\s*keyword/i,
                /up\s*to\s*(\d+)\s*keyword/i,
                /(\d+)\s*keyword/i,
                /unlimited/i,
                /(\d+)\s*apps?:?\s*upto\s*(\d+)\s*keyword/i,
                /(\d+)-app\s*upto\s*(\d+)\s*keyword/i,
            ]
            for (const pattern of patterns) {
                const match = benefit.benefit_value.match(pattern)
                if (match) return match[match.length - 1] || 'Unlimited'
            }
        }
        return '5'
    }

    const isUpgradeError = error => {
        const response = error?.response
        const data = response?.data || {}
        const message = data?.message || ""
        const action = data?.action || ""
        return (
            response?.status === 400 &&
            (action === "UPGRADE" || /max limit/i.test(message))
        )
    }

    const isUpgradeResponse = response => {
        if (!response || typeof response !== "object") return false

        // If using axios, real payload may be in response.data
        const data = response?.data || response

        const status = data?.status
        const action = data?.action
        const message = data?.message || ""

        return (
            (status === 400 || status === 500) &&
            (action === "UPGRADE" || action === "ERROR" || /max limit|limit.*tracking/i.test(message))
        )
    }

    // Update the tracked keywords display to use the filtered keywords
    useEffect(() => {
        // Store the keywordsByCountryLanguage in localStorage
        storeValue(
            item_id,
            "keywords_by_country_language",
            keywordsByCountryLanguage
        )
    }, [item_id, keywordsByCountryLanguage])

    // Add a ref to track API calls and prevent duplicates
    const apiCallTracker = useRef({})

    // Add a ref to track keyword tracking API calls
    const keywordTrackingCallTracker = useRef({})

    // Add a ref to track the last selected pair to prevent redundant API calls when switching tabs
    const lastSelectedPairRef = useRef(null)

    const {
        countriesData,
        predefinedKeywords,
        keywordsLoading,
        trackedApp,
        trackedKeywordSummary,
        summaryLoaded,
        keywordTrackingData,
        addingKeyword,
        fetchKeywordTracking,
        addCategoryTracking,
        removeEntity,
        notFoundError,
    } = useKeywordTrackingAPI(item_id, getStoredCountriesData())

    // URL Validation and Correction
    useEffect(() => {
        if (trackedApp && trackedApp.name) {
            const expectedSlug = generateSlug(trackedApp.name);

            // If no slug in URL or slug mismatch, redirect to correct URL
            if (!appName || appName !== expectedSlug) {
                navigate(`/xr/category/${expectedSlug}?id=${item_id}`, {
                    replace: true,
                    state: location.state, // Preserve state
                })
            }
        }
    }, [trackedApp, appName, navigate, location.state, item_id]);

    // Add debugging for date range
    useEffect(() => {
        console.log("Date range changed:", { fromDate, toDate })
    }, [fromDate, toDate])

    // Add debugging for predefined keywords
    useEffect(() => {
        console.log("Predefined keywords loaded:", predefinedKeywords.length)
    }, [predefinedKeywords])

    // Trigger data refresh when date range changes
    useEffect(() => {
        if (selectedCountry && selectedLanguage && initialFetchDone) {
            // Reset API call tracker for current country/language
            const callKey = `${selectedCountry}_${selectedLanguage}`
            apiCallTracker.current[callKey] = false

            // Trigger data fetch with new date range
            fetchKeywordTracking(selectedCountry, selectedLanguage, fromDate, toDate)
                .then(data => {
                    handleKeywordTrackingResponse(data, selectedCountry, selectedLanguage)
                })
                .catch(error => {
                    console.error("Error fetching data with new date range:", error)
                })
        }
    }, [fromDate, toDate, selectedCountry, selectedLanguage, initialFetchDone])


    useEffect(() => {
        if (location.state?.item_id) {
            localStorage.setItem("tracked_item_id", location.state.item_id)
        }
    }, [location.state])

    // Modify the useEffect that handles the initial API response
    useEffect(() => {
        // When countriesData is loaded from the API, store it in localStorage
        if (countriesData.length > 0 && !countriesDataLoaded) {
            storeCountriesData(countriesData)
            setCountriesDataLoaded(true)
        }
    }, [countriesData, countriesDataLoaded])

    // Set default selection when tracked data is available
    useEffect(() => {
        // This effect now only ensures countries data is loaded but does NOT auto-select
        // Countries and languages should only be selected when user explicitly chooses them
        // or when restoring from localStorage (which is handled in useState initializers)
    }, [countriesData])

    // Load available languages for the selected country when countries data is loaded
    useEffect(() => {
        if (countriesData.length > 0 && selectedCountry) {
            const found = countriesData.find(c => c.name === selectedCountry)
            if (found) {
                setAvailableLanguages(found.locales || [])
            }
        }
    }, [countriesData, selectedCountry])

    // Set initialFetchDone to true when both country and language are selected initially
    useEffect(() => {
        if (selectedCountry && selectedLanguage && !initialFetchDone) {
            // If we have selections, set initialFetchDone to true to trigger data fetch
            setInitialFetchDone(true)
        }
    }, [selectedCountry, selectedLanguage, initialFetchDone])

    useEffect(() => {
        if (trackedCountries.length > 0 && !activeTrackedCountry) {
            setActiveTrackedCountry(trackedCountries[0])
        }
    }, [trackedCountries, activeTrackedCountry])

    const appMetadata = useMemo(() => {
        const genreField = trackedApp?.genre
        const categoryField = trackedApp?.category
        let genres = []
        if (genreField) {
            genres = Array.isArray(genreField) ? genreField : [genreField]
        } else if (categoryField) {
            genres = [categoryField]
        }
        return {
            genres,
            game_modes: trackedApp?.game_modes || [],
            description: trackedApp?.description || "",
            keywords: trackedKeywords || []
        }
    }, [trackedApp, trackedKeywords])

    // Store values in refs to avoid dependency changes triggering re-renders
    const trackedKeywordsRef = useRef(trackedKeywords)
    const activeKeywordRef = useRef(activeKeyword)
    const itemIdRef = useRef(item_id)
    const fetchKeywordTrackingRef = useRef(fetchKeywordTracking)
    const addCategoryTrackingRef = useRef(addCategoryTracking)
    const debouncedFetchRef = useRef(null)

    // Create a function to handle the API response and update state
    const handleKeywordTrackingResponse = useCallback(
        (data, country, language) => {
            if (!data) return

            const countryName = getCountryName(country, countriesData)

            if (data && typeof data === "object") {
                const pairKey = `${countryName}_${language}`
                const keywords = Object.keys(data).filter(key => key !== "game_data")

                if (keywords.length > 0) {
                    const pair = `${countryName} (${language})`

                    setTrackedCountryLanguagePairs(prev => {
                        if (prev.some(p => p === pair)) return prev
                        const merged = Array.from(new Set([...prev, pair]))
                        console.log("LOGGINGGGG HEREEEE 731")

                        storeValue(item_id, "tracked_country_language_pairs", merged)
                        return merged
                    })

                    setActiveTrackedPair(pair)

                    setTrackedCountries(prev => {
                        if (prev.includes(countryName)) return prev
                        return [...prev, countryName]
                    })
                }

                setKeywordsByCountryLanguage(prev => {
                    const existingKeywords = prev[pairKey] || []
                    const mergedKeywords = [
                        ...new Set([...existingKeywords, ...keywords]),
                    ]
                    const updated = {
                        ...prev,
                        [pairKey]: mergedKeywords,
                    }
                    storeValue(item_id, "keywords_by_country_language", updated)
                    return updated
                })
            }
        },
        [countriesData, item_id]
    )

    // Update refs when values change
    useEffect(() => {
        trackedKeywordsRef.current = trackedKeywords
        activeKeywordRef.current = activeKeyword
        itemIdRef.current = item_id
        fetchKeywordTrackingRef.current = fetchKeywordTracking
        addCategoryTrackingRef.current = addCategoryTracking

        // Create a debounced version of fetchKeywordTracking that also handles the response
        debouncedFetchRef.current = debounce((country, language) => {
            fetchKeywordTracking(country, language, fromDate, toDate)
                .then(data => {
                    handleKeywordTrackingResponse(data, country, language)
                })
                .catch(error => {
                    console.error("Error fetching tracking data:", error)
                    // Reset the tracker on error so we can try again
                    const callKey = `${country}_${language}`
                    apiCallTracker.current[callKey] = false
                })
        }, 300) // 300ms debounce time
    }, [
        trackedKeywords,
        activeKeyword,
        item_id,
        fetchKeywordTracking,
        addCategoryTracking,
        handleKeywordTrackingResponse,
        fromDate,
        toDate,
    ])

    // Initial data fetch on component mount
    useEffect(() => {
        // Only run once when the component mounts and we have an item_id
        if (item_id) {
            // Make initial API call without country/language
            fetchKeywordTracking(null, null, fromDate, toDate)
                .then(response => {
                    // The response format is expected to be:
                    // { status: 200, message: "...", action: "DISPLAY", data: { "keyword1": {...}, "keyword2": {...} } }
                    if (response && response.status === 200 && response.data) {
                        const data = response.data

                        // Extract keywords from the response, filtering out game_data
                        const keywords = Object.keys(data).filter(
                            key => key !== "game_data"
                        )
                        if (keywords.length > 0) {
                            // Merge with existing tracked keywords from localStorage
                            const mergedKeywords = [
                                ...new Set([...trackedKeywords, ...keywords]),
                            ]
                            setTrackedKeywords(mergedKeywords)
                            storeValue(item_id, "tracked_keywords", mergedKeywords)

                            // If no active keyword is set, use the first one from the API
                            if (!activeKeyword && keywords.length > 0) {
                                setActiveKeyword(keywords[0])
                                storeValue(item_id, "active_keyword", keywords[0])
                            }

                            // Extract countries and languages from the response
                            const countryLanguagePairs = new Set()
                            const countries = new Set()

                            Object.values(data).forEach(keywordData => {
                                if (keywordData.country && keywordData.language) {
                                    // Convert country code to full name for display
                                    const countryName = getCountryName(keywordData.country, countriesData)
                                    const pair = `${countryName} (${keywordData.language})`
                                    countryLanguagePairs.add(pair)

                                    // Also track the country separately (convert to full name)
                                    countries.add(countryName)
                                }
                            })

                            // Update tracked country-language pairs
                            if (countryLanguagePairs.size > 0) {
                                const pairsList = Array.from(countryLanguagePairs)

                                // Merge with existing pairs from localStorage
                                setTrackedCountryLanguagePairs(prev => {
                                    const merged = Array.from(new Set([...prev, ...pairsList]))
                                    storeValue(item_id, "tracked_country_language_pairs", merged)
                                    console.log("LOGGINGGGG HEREEEE 848")

                                    // Don't auto-select here to avoid race conditions.
                                    // We use a dedicated useEffect for auto-selection based on trackedCountryLanguagePairs
                                    return merged
                                })
                            }

                            // Update tracked countries
                            if (countries.size > 0) {
                                const countriesList = Array.from(countries)
                                setTrackedCountries(prev => {
                                    const merged = [...new Set([...prev, ...countriesList])]
                                    storeValue(item_id, "tracked_countries", merged)
                                    return merged
                                })
                            }

                            // Map each category to its own country-language pair.
                            // Previously this assigned ALL categories to every pair, which
                            // caused stillNeededElsewhere checks to always return true and
                            // broke per-pair removal.
                            setKeywordsByCountryLanguage(prev => {
                                const updated = { ...prev }

                                Object.entries(data).forEach(([category, keywordData]) => {
                                    if (category === "game_data") return
                                    if (keywordData && keywordData.country && keywordData.language) {
                                        const countryName = getCountryName(keywordData.country, countriesData)
                                        const pairKey = `${countryName}_${keywordData.language}`
                                        if (!updated[pairKey]) updated[pairKey] = []
                                        if (!updated[pairKey].includes(category)) updated[pairKey].push(category)
                                    }
                                })

                                storeValue(item_id, "keywords_by_country_language", updated)
                                return updated
                            })
                        }
                    }
                })
                .catch(error => {
                    console.error("Error in initial API call on mount:", error)
                })
                .finally(() => {
                    setCategoryTrackingDataLoaded(true)
                })
        } else {
            // No item_id to fetch for — nothing to wait on.
            setCategoryTrackingDataLoaded(true)
        }
        // Only depend on item_id and fetchKeywordTracking to prevent multiple calls
        // But we also want to re-evaluate when countriesData loads to ensure proper naming
    }, [item_id, fetchKeywordTracking, countriesData.length])

    // Seed Tracked Data Availability from the full per-game locale list, so
    // locales without keyword data yet (from get_keyword_tracking) still show up
    useEffect(() => {
        const localDetails = trackedKeywordSummary?.local_details
        if (!localDetails || localDetails.length === 0 || countriesData.length === 0) return

        const countryLanguagePairs = new Set()
        const countries = new Set()
        localDetails.forEach(detail => {
            if (detail.country && detail.language) {
                const countryName = getCountryName(detail.country, countriesData)
                countryLanguagePairs.add(`${countryName} (${detail.language})`)
                countries.add(countryName)
            }
        })

        if (countryLanguagePairs.size > 0) {
            const summaryPairsList = Array.from(countryLanguagePairs)
            setTrackedCountryLanguagePairs(prev => {
                const merged = [...new Set([...prev, ...summaryPairsList])]
                storeValue(item_id, "tracked_country_language_pairs", merged)
                return merged
            })
        }

        if (countries.size > 0) {
            const summaryCountriesList = Array.from(countries)
            setTrackedCountries(prev => {
                const merged = [...new Set([...prev, ...summaryCountriesList])]
                storeValue(item_id, "tracked_countries", merged)
                return merged
            })
        }
    }, [trackedKeywordSummary, countriesData, item_id])

    // 🤖 Auto-select the first tracked pair if none is active
    useEffect(() => {
        // Only run if we have tracked pairs but no active selection
        if (trackedCountryLanguagePairs.length > 0 && !activeTrackedPair && !selectedCountry) {
            const firstPair = trackedCountryLanguagePairs[0]

            // Basic validation format check
            const match = firstPair.match(/(.*) \((.*)\)/)

            if (match) {
                // Log for debugging
                console.log("Auto-selecting first tracked pair:", firstPair)

                setActiveTrackedPair(firstPair)
                storeValue(item_id, "active_tracked_pair", firstPair)

                const country = match[1]
                const language = match[2]

                setSelectedCountry(country)
                setSelectedLanguage(language)
                storeValue(item_id, "selected_country", country)
                storeValue(item_id, "selected_language", language)

                // Also set active tracked country
                setActiveTrackedCountry(country)
                storeValue(item_id, "active_tracked_country", country)

                // IMPORTANT: Trigger the data fetch
                // We defer this slightly to ensure state updates propagate
                setTimeout(() => {
                    setInitialFetchDone(true)
                }, 0)
            }
        }
    }, [trackedCountryLanguagePairs, activeTrackedPair, selectedCountry, item_id])

    // 🧠 Fetch keyword tracking when country and language are selected - with duplicate prevention
    useEffect(() => {
        // Skip if we don't have necessary data
        if (!selectedCountry || !selectedLanguage || !initialFetchDone) {
            return
        }

        // Create a unique key for this country/language combination
        const callKey = `${selectedCountry}_${selectedLanguage}`
        const currentPair = `${selectedCountry}_${selectedLanguage}`

        // Skip if this is the same as the last selected pair (prevents redundant calls when clicking tabs)
        if (lastSelectedPairRef.current === currentPair) {
            return
        }

        // Update the last selected pair
        lastSelectedPairRef.current = currentPair

        // Check if we've already made this exact API call recently
        if (apiCallTracker.current[callKey]) {
            return
        }

        // Mark that we're making this API call
        apiCallTracker.current[callKey] = true

        // Use direct fetchKeywordTracking to ensure immediate response
        fetchKeywordTracking(selectedCountry, selectedLanguage, fromDate, toDate)
            .then(data => {
                // Process the response data
                handleKeywordTrackingResponse(data, selectedCountry, selectedLanguage)
            })
            .catch(error => {
                console.error("Error fetching tracking data:", error)
                // Reset the tracker on error so we can try again
                apiCallTracker.current[callKey] = false
            })

        // IMPORTANT: Removed the automatic adding of keywords when changing country/language
        // This was causing excessive API calls
    }, [
        selectedCountry,
        selectedLanguage,
        initialFetchDone,
        keywordsByCountryLanguage,
        fetchKeywordTracking,
        handleKeywordTrackingResponse,
    ])

    // Restore active keyword when keywords are available
    useEffect(() => {
        const currentPairKey = getCurrentPairKey()
        if (
            currentPairKey &&
            keywordsByCountryLanguage[currentPairKey] &&
            keywordsByCountryLanguage[currentPairKey].length > 0
        ) {
            // If we have keywords for the current pair but no active keyword, set the first one as active
            if (!activeKeyword) {
                const firstKeyword = keywordsByCountryLanguage[currentPairKey].find(
                    k => k !== "game_data"
                )
                if (firstKeyword) {
                    setActiveKeyword(firstKeyword)
                    storeValue(item_id, "active_keyword", firstKeyword)
                }
            }
        }
    }, [
        keywordsByCountryLanguage,
        selectedCountry,
        selectedLanguage,
        activeKeyword,
        item_id,
    ])

    // Additional effect to ensure predefined keywords stay selected
    useEffect(() => {
        // If we have an active keyword from localStorage and it's a predefined keyword, keep it selected
        if (activeKeyword && predefinedKeywords.includes(activeKeyword)) {
            storeValue(item_id, "active_keyword", activeKeyword)
        }
    }, [activeKeyword, predefinedKeywords, item_id])

    // Update the handleCountryChange function to be more efficient
    const handleCountryChange = e => {
        const selected = typeof e === 'string' ? e : e.target.value
        setSelectedCountry(selected)
        setSelectionError(false)

        // Update available languages for the selected country
        if (selected) {
            const found = countriesData.find(c => c.name === selected)
            if (found) {
                const languages = found.locales || []
                setAvailableLanguages(languages)

                // Select the first language if available
                if (languages.length > 0) {
                    const firstLanguage = languages[0]
                    setSelectedLanguage(firstLanguage)

                    // Reset API call tracker for this new country/language pair
                    const callKey = `${selected}_${firstLanguage}`
                    apiCallTracker.current[callKey] = false

                    // Set initialFetchDone to trigger a fresh API call
                    setInitialFetchDone(true)

                    trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_LOCALE_CHANGED, {
                        country: selected,
                        language: firstLanguage,
                    })

                } else {
                    setSelectedLanguage("")
                    trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_LOCALE_CHANGED, {
                        country: selected,
                        language: "",
                    })
                }
            }

            // Set this as the active tracked country
            setActiveTrackedCountry(selected)
        } else {
            setAvailableLanguages([])
            setSelectedLanguage("")
        }

        setActiveKeyword(null)

        // Reset all API call trackers for this country
        Object.keys(apiCallTracker.current).forEach(key => {
            if (key.startsWith(selected)) {
                apiCallTracker.current[key] = false
            }
        })

        // Reset tracked keyword trackers for this country to prevent auto-adding
        Object.keys(keywordTrackingCallTracker.current).forEach(key => {
            if (key.includes(`_${selected}_`)) {
                keywordTrackingCallTracker.current[key] = false
            }
        })
    }

    const handleCountrySelect = country => {
        if (!country) {
            setAvailableLanguages([])
            return
        }
        const found = countriesData.find(c => c.name === country)
        if (found) {
            setAvailableLanguages(found.locales || [])
        } else {
            setAvailableLanguages([])
        }
    }

    // Update the handleLanguageChange function to be more efficient
    // Accepts either a DOM event (legacy <select> callers) or a plain string
    // (FlagSelectDropdown, which reports the chosen value directly).
    const handleLanguageChange = e => {
        const selectedLang = typeof e === 'string' ? e : e.target.value
        setSelectedLanguage(selectedLang)
        setSelectionError(false)

        setActiveKeyword(null)

        // Reset API call tracker for the current country and new language
        if (selectedCountry) {
            const callKey = `${selectedCountry}_${selectedLang}`
            apiCallTracker.current[callKey] = false

            // Set initialFetchDone to trigger a fresh API call
            setInitialFetchDone(true)

            trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_LOCALE_CHANGED, {
                country: selectedCountry,
                language: selectedLang,
            })
        }
    }

    // Update the handleTrackedCountryClick function to prevent multiple API calls
    const handleTrackedCountryClick = useCallback(
        country => {
            // Skip if this is already the active country
            if (activeTrackedCountry === country) {
                return
            }

            // Update the UI immediately
            setActiveTrackedCountry(country)

            // Update country and related state with a slight delay to prevent multiple API calls
            setTimeout(() => {
                setSelectedCountry(country)

                // Update available languages for this country
                const found = countriesData.find(c => c.name === country)
                if (found) {
                    const languages = found.locales || []
                    setAvailableLanguages(languages)

                    // Select the first language if available
                    if (languages.length > 0) {
                        const firstLanguage = languages[0]
                        setSelectedLanguage(firstLanguage)

                        // Reset API call tracker for this country/language
                        const callKey = `${country}_${firstLanguage}`
                        apiCallTracker.current[callKey] = false

                        setInitialFetchDone(true) // Trigger data fetch

                    }
                }
            }, 50) // Small delay to batch state updates
        },
        [countriesData, activeTrackedCountry]
    )

    // Update the handleTrackedPairClick function to prevent multiple API calls
    const handleTrackedPairClick = useCallback(
        pair => {
            // Extract country and language from the pair
            // Format is "Country (Language)"
            const match = pair.match(/(.*) \((.*)\)/)
            if (match) {
                const country = match[1]
                const language = match[2]

                // Skip if this is already the active pair
                if (activeTrackedPair === pair) {
                    return
                }

                // Update the UI immediately
                setActiveTrackedPair(pair)
                setActiveTrackedCountry(country)
                setShowAllKeywords(false)

                // Update country and language with a slight delay to prevent multiple API calls
                // when rapidly clicking tabs
                setTimeout(() => {
                    setSelectedCountry(country)
                    setSelectedLanguage(language)

                    // Update available languages for this country
                    const found = countriesData.find(c => c.name === country)
                    if (found) {
                        setAvailableLanguages(found.locales || [])
                    }

                    // Reset API call tracker for this country/language
                    const callKey = `${country}_${language}`
                    apiCallTracker.current[callKey] = false

                    setInitialFetchDone(true) // Trigger data fetch

                }, 50) // Small delay to batch state updates
            }
        },
        [countriesData, activeTrackedPair]
    )

    // Helper function to update keyword state
    // const updateKeywordState = async keyword => {
    //   // Check if keyword is already in tracked keywords
    //   if (trackedKeywords.includes(keyword)) {
    //     return
    //   }

    //   // Add the keyword to trackedKeywords if not already there
    //   const updatedKeywords = [...trackedKeywords, keyword]
    //   setTrackedKeywords(updatedKeywords)
    //   storeValue(item_id, "tracked_keywords", updatedKeywords)

    //   // Add the keyword to the specific country-language pair
    //   const pairKey = `${selectedCountry}_${selectedLanguage}`

    //   setKeywordsByCountryLanguage(prev => {
    //     const existingKeywords = prev[pairKey] || []

    //     // Check if keyword is already in this country-language pair
    //     if (existingKeywords.includes(keyword)) {
    //       return prev
    //     }

    //     const updated = {
    //       ...prev,
    //       [pairKey]: [...existingKeywords, keyword],
    //     }
    //     return updated
    //   })

    //   // Create a country-language pair
    //   const pair = `${selectedCountry} (${selectedLanguage})`

    //   // Add the pair if not already tracked - prevent duplicates
    //   if (!trackedCountryLanguagePairs.some(p => p === pair)) {
    //     const updatedPairs = [...trackedCountryLanguagePairs, pair]
    //     setTrackedCountryLanguagePairs(updatedPairs)
    //     storeValue(item_id, "tracked_country_language_pairs", updatedPairs)
    //   }

    //   // Set as active pair
    //   setActiveTrackedPair(pair)

    //   // Add the selected country to trackedCountries if not already there
    //   if (!trackedCountries.includes(selectedCountry)) {
    //     const updatedCountries = [...trackedCountries, selectedCountry]
    //     setTrackedCountries(updatedCountries)
    //     storeValue(item_id, "tracked_countries", updatedCountries)
    //   }

    //   setActiveKeyword(keyword)

    //   // Reset the API call tracker for this country/language to force a refresh
    //   // But don't trigger the API call directly - let the useEffect handle it
    //   const callKey = `${selectedCountry}_${selectedLanguage}`
    //   apiCallTracker.current[callKey] = false
    //   setInitialFetchDone(true)
    // }

    // Update the handleKeywordClick function to be more efficient
    const handleKeywordClick = async keyword => {
        if (keyword === "game_data") return
        if (!selectedCountry || !selectedLanguage) {
            setSelectionError(true)
            return
        }
        setSelectionError(false)

        setActiveKeyword(keyword)
        storeValue(item_id, "active_keyword", keyword)

        trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_GENRE_VIEWED, {
            genre: keyword,
            country: selectedCountry,
            language: selectedLanguage,
        })

        const keywordTrackingKey = `${keyword}_${selectedCountry}_${selectedLanguage}`
        if (keywordTrackingCallTracker.current[keywordTrackingKey]) return

        keywordTrackingCallTracker.current[keywordTrackingKey] = true

        try {
            const response = await addCategoryTracking(
                keyword,
                selectedCountry,
                selectedLanguage,
                fromDate,
                toDate,
                () => triggerToast(`"${keyword}" is taking longer than expected. Data will appear once ready.`, "warning")
            )

            if (isUpgradeResponse(response)) {
                setShowUpgradeModal(true)
                return
            }

            await updateKeywordState(keyword)
            // Notify other pages (landing page) that a category keyword was tracked for this item
            try {
                window.dispatchEvent(
                    new CustomEvent("keywordTracked", { detail: { item_id, keyword } })
                )
            } catch (err) {
                // ignore dispatch errors
            }
        } catch (error) {
            console.error("Error tracking keyword:", error)
            await updateKeywordState(keyword)
            // Even on fallback/local add, dispatch so landing page can refresh
            try {
                window.dispatchEvent(
                    new CustomEvent("keywordTracked", { detail: { item_id, keyword } })
                )
            } catch (err) {
                // ignore
            }
        } finally {
            keywordTrackingCallTracker.current[keywordTrackingKey] = false
        }
    }

    // Update the handleAddKeyword function to be more efficient
    // const handleAddKeyword = async () => {
    //   const keyword = searchInput.trim()
    //   if (
    //     !keyword ||
    //     !selectedCountry ||
    //     !selectedLanguage ||
    //     keyword === "game_data"
    //   ) {
    //     setSearchInput("")
    //     return
    //   }

    //   if (trackedKeywords.includes(keyword)) {
    //     console.log("Keyword already tracked, skipping addition")
    //     setSearchInput("")
    //     setActiveKeyword(keyword)
    //     return
    //   }

    //   const keywordTrackingKey = `${keyword}_${selectedCountry}_${selectedLanguage}`

    //   // Prevent duplicate API calls
    //   if (keywordTrackingCallTracker.current[keywordTrackingKey]) {
    //     console.log("Duplicate addCategoryTracking call skipped for:", keyword)
    //     setSearchInput("")
    //     return
    //   }

    //   keywordTrackingCallTracker.current[keywordTrackingKey] = true

    //   try {
    //     const response = await addCategoryTracking(
    //       keyword,
    //       selectedCountry,
    //       selectedLanguage,
    //       fromDate,
    //       toDate
    //     )

    //     if (isUpgradeResponse(response)) {
    //       console.log("Upgrade required - showing modal")
    //       setShowUpgradeModal(true)
    //       return // stop further processing
    //     }

    //     await updateKeywordState(keyword)
    //     // Notify landing page / other components that a category keyword was tracked
    //     try {
    //       window.dispatchEvent(
    //         new CustomEvent("keywordTracked", {detail: {item_id, keyword} })
    //       )
    //     } catch (err) {
    //       // ignore
    //     }
    //   } catch (error) {
    //     console.error("Error adding keyword:", error)
    //     await updateKeywordState(keyword) // fallback: add locally
    //     try {
    //       window.dispatchEvent(
    //         new CustomEvent("keywordTracked", {detail: {item_id, keyword} })
    //       )
    //     } catch (err) {
    //       // ignore
    //     }
    //   } finally {
    //     keywordTrackingCallTracker.current[keywordTrackingKey] = false
    //     setSearchInput("")
    //   }
    // }


    // Updated helper function to batch update keyword/category state
    const updateKeywordState = (keywords) => {
        const keywordsToAdd = Array.isArray(keywords) ? keywords : [keywords];
        const pair = `${selectedCountry} (${selectedLanguage})`;
        const pairKey = `${selectedCountry}_${selectedLanguage}`;

        setTrackedKeywords(prev => {
            const fresh = keywordsToAdd.filter(k => !prev.includes(k));
            if (fresh.length === 0) return prev;
            const updated = [...prev, ...fresh];
            storeValue(item_id, "tracked_keywords", updated);
            return updated;
        });

        setKeywordsByCountryLanguage(prev => {
            const existingKeywords = prev[pairKey] || [];
            const fresh = keywordsToAdd.filter(k => !existingKeywords.includes(k));
            if (fresh.length === 0) return prev;
            const updated = { ...prev, [pairKey]: [...existingKeywords, ...fresh] };
            storeValue(item_id, "keywords_by_country_language", updated);
            return updated;
        });

        setTrackedCountryLanguagePairs(prev => {
            if (prev.includes(pair)) return prev;
            const updated = [...prev, pair];
            console.log("LOGGINGGGG HEREEEE 1403");

            storeValue(item_id, "tracked_country_language_pairs", updated);
            return updated;
        });

        setActiveTrackedPair(pair);

        if (keywordsToAdd.length > 0) {
            setActiveKeyword(keywordsToAdd[0]);
        }

        setTrackedCountries(prev => {
            if (prev.includes(selectedCountry)) return prev;
            const updated = [...prev, selectedCountry];
            storeValue(item_id, "tracked_countries", updated);
            return updated;
        });

        const callKey = `${selectedCountry}_${selectedLanguage}`;
        apiCallTracker.current[callKey] = false;
        setInitialFetchDone(true);
    };

    const handleAddKeyword = async (manualGenre = null) => {
        const inputToProcess = manualGenre || searchInput;

        if (!inputToProcess) {
            setSearchInput("");
            return;
        }
        if (!selectedCountry || !selectedLanguage) {
            setSearchInput("");
            triggerToast("Please select a country and language before adding genres.", "error");
            return;
        }

        const keywords = String(inputToProcess)
            .split(",")
            .map(k => k.trim())
            .filter(k => k && k !== "game_data");

        if (keywords.length === 0) {
            setSearchInput("");
            return;
        }

        const uniqueKeywords = [...new Set(keywords)];

        setIsTrackingInProgress(true);

        // Optimistic update to reflect in UI immediately, mark as pending
        const pairLabelCat = `${selectedCountry} (${selectedLanguage})`;
        const isPairNewCat = !trackedCountryLanguagePairs.includes(pairLabelCat);
        if (isPairNewCat) {
            setPendingPairs(prev => new Set([...prev, pairLabelCat]));
        }
        updateKeywordState(uniqueKeywords);
        setPendingKeywords(prev => new Set([...prev, ...uniqueKeywords]));

        // Clear search input immediately for better UX
        if (!manualGenre) setSearchInput("");

        const apiCalls = uniqueKeywords.map(async (keyword) => {
            const key = `${keyword}_${selectedCountry}_${selectedLanguage}`;
            if (keywordTrackingCallTracker.current[key]) return null;

            keywordTrackingCallTracker.current[key] = true;

            try {
                const response = await addCategoryTracking(
                    keyword,
                    selectedCountry,
                    selectedLanguage,
                    fromDate,
                    toDate,
                    () => triggerToast(`"${keyword}" is taking longer than expected. Data will appear once ready.`, "warning")
                );

                if (isUpgradeResponse(response)) {
                    setShowUpgradeModal(true);
                    return null;
                }

                return keyword;
            } catch (err) {
                console.error("Error tracking category keyword:", err);
                return keyword; // fallback to local add
            } finally {
                keywordTrackingCallTracker.current[key] = false;
            }
        });

        const results = await Promise.allSettled(apiCalls);

        const successfullyAdded = results
            .filter(r => r.status === "fulfilled" && r.value)
            .map(r => r.value);

        setPendingKeywords(new Set());
        setPendingPairs(new Set());
        setIsTrackingInProgress(false);

        // Roll back genres that failed the API call
        const failedGenres = uniqueKeywords.filter(k => !successfullyAdded.includes(k));
        if (failedGenres.length > 0) {
            const pairKeyRollback = `${selectedCountry}_${selectedLanguage}`;
            setTrackedKeywords(prev => prev.filter(k => !failedGenres.includes(k)));
            setKeywordsByCountryLanguage(prev => {
                const existing = prev[pairKeyRollback] || [];
                return { ...prev, [pairKeyRollback]: existing.filter(k => !failedGenres.includes(k)) };
            });
        }

        // Roll back pair tab if all genres failed and pair was newly added
        if (successfullyAdded.length === 0 && isPairNewCat) {
            setTrackedCountryLanguagePairs(prev => prev.filter(p => p !== pairLabelCat));
        }

        if (successfullyAdded.length > 0) {
            // Already updated state optimistically, but we call it again to ensure persistence and correct tracking
            await updateKeywordState(successfullyAdded);

            trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_GENRES_ADDED, {
                genre_count: successfullyAdded.length,
                country: selectedCountry,
                language: selectedLanguage,
                source: manualGenre ? "manual" : "search",
            });

            // Refresh tracking data to update the table values (rankings, etc.)
            if (selectedCountry && selectedLanguage) {
                try {
                    await fetchKeywordTracking(selectedCountry, selectedLanguage, fromDate, toDate);
                } catch (error) {
                    console.error("Error refreshing tracking data after adding categories:", error);
                }
            }

            successfullyAdded.forEach(keyword => {
                try {
                    window.dispatchEvent(
                        new CustomEvent("keywordTracked", { detail: { item_id, keyword } })
                    );
                } catch (err) {
                    // ignore
                }
            });
        }
    };


    // Handle removing a keyword
    const removeAppFromTracking = async () => {
        const success = await removeEntity(null, "category")
        if (success) {
            try {
                const prefix = `category_tracking_${item_id}_`
                Object.keys(localStorage).forEach(key => {
                    if (key.startsWith(prefix)) localStorage.removeItem(key)
                })
            } catch (e) { }
            apiCallTracker.current = {}
            navigate("/category-tracking")
        }
    }

    const handleRemoveEntity = async (id) => {
        if (!id && !item_id) return;

        // Call backend to remove the category — scoped to the current country/language pair
        const success = await removeEntity(id, "category", id ? {
            country: selectedCountry,
            language: selectedLanguage,
        } : {});

        if (success) {
            if (!id) {
                trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_GENRE_REMOVED, {
                    country: selectedCountry,
                    language: selectedLanguage,
                    scope: "app",
                });

                // Removing entire app - clear all stored data for this app
                Object.keys(localStorage).forEach((key) => {
                    if (key.startsWith(`category_tracking_${item_id}`)) {
                        localStorage.removeItem(key);
                    }
                });

                // Clear API call tracker
                apiCallTracker.current = {};

                navigate("/category-tracking");
            } else {
                trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_GENRE_REMOVED, {
                    genre: id,
                    country: selectedCountry,
                    language: selectedLanguage,
                });

                // Remove from trackedKeywords only if no other pair still references this category
                const currentPairKey = selectedCountry && selectedLanguage ? `${selectedCountry}_${selectedLanguage}` : null
                const otherPairs = { ...keywordsByCountryLanguage }
                if (currentPairKey) delete otherPairs[currentPairKey]
                const stillNeededElsewhere = Object.values(otherPairs).flat().includes(id)

                if (!stillNeededElsewhere) {
                    const updated = trackedKeywords.filter((k) => k !== id)
                    setTrackedKeywords(updated)
                    storeValue(item_id, "tracked_keywords", updated)
                }

                // Remove from current country-language pair and check if it becomes empty
                let pairBecameEmpty = false;
                let nextBest = null;

                if (selectedCountry && selectedLanguage) {
                    const pairKey = `${selectedCountry}_${selectedLanguage}`;

                    // Calculate what the updated state will look like
                    const updatedCategories = (keywordsByCountryLanguage[pairKey] || []).filter((k) => k !== id);

                    // Check if this pair will become empty
                    if (updatedCategories.length === 0) {
                        pairBecameEmpty = true;

                        // Calculate next best pair BEFORE updating state
                        const remainingPairs = { ...keywordsByCountryLanguage };
                        delete remainingPairs[pairKey];

                        // Find the pair with most categories from remaining pairs
                        let maxCount = 0;
                        let bestPairKey = null;

                        Object.entries(remainingPairs).forEach(([key, keywords]) => {
                            const count = keywords.length;
                            if (count > maxCount) {
                                maxCount = count;
                                bestPairKey = key;
                            }
                        });

                        if (bestPairKey) {
                            const parts = bestPairKey.split('_')
                            const country = parts[0]
                            const language = parts.slice(1).join('_')
                            nextBest = { pair: `${country} (${language})`, country, language };
                        }
                    }

                    // Now update the state
                    setKeywordsByCountryLanguage((prev) => {
                        const updated = {
                            ...prev,
                            [pairKey]: updatedCategories
                        };

                        if (pairBecameEmpty) {
                            delete updated[pairKey];
                        }

                        storeValue(item_id, "keywords_by_country_language", updated);
                        return updated;
                    });

                    // If pair became empty, remove it from trackedCountryLanguagePairs
                    if (pairBecameEmpty) {
                        const pair = `${selectedCountry} (${selectedLanguage})`;
                        setTrackedCountryLanguagePairs(prev => {
                            const updated = prev.filter(p => p !== pair);
                            console.log("LOGGINGGGG HEREEEE 1656");

                            storeValue(item_id, "tracked_country_language_pairs", updated);
                            return updated;
                        });

                        // Auto-select the next best pair (calculated earlier)
                        if (nextBest) {
                            setActiveTrackedPair(nextBest.pair);
                            setSelectedCountry(nextBest.country);
                            setSelectedLanguage(nextBest.language);
                            storeValue(item_id, "active_tracked_pair", nextBest.pair);
                            storeValue(item_id, "selected_country", nextBest.country);
                            storeValue(item_id, "selected_language", nextBest.language);
                        } else {
                            // No pairs left — app has zero tracked genres, remove it from tracking
                            await removeAppFromTracking()
                            return
                        }
                    }
                }

                // If this was the active category, clear it
                if (activeKeyword === id) {
                    setActiveKeyword(null);
                }

                // Reset API call tracker for current country/language
                if (selectedCountry && selectedLanguage && !pairBecameEmpty) {
                    const callKey = `${selectedCountry}_${selectedLanguage}`;
                    apiCallTracker.current[callKey] = false;
                }

                // Refresh tracking data for the current country/language (only if pair still exists)
                if (!pairBecameEmpty && selectedCountry && selectedLanguage) {
                    await fetchKeywordTracking(selectedCountry, selectedLanguage, fromDate, toDate);
                }
            }
        } else {
            console.error("Failed to remove category on backend.");
        }
    };

    const [genresCopied, setGenresCopied] = useState(false)

    const handleCopyGenres = () => {
        const keywords = getKeywordsForCurrentPair()
        if (keywords.length === 0) return
        navigator.clipboard.writeText(keywords.join(', ')).then(() => {
            setGenresCopied(true)
            setTimeout(() => setGenresCopied(false), 2000)
        }).catch(() => {
            const textArea = document.createElement('textarea')
            textArea.value = keywords.join(', ')
            textArea.style.position = 'fixed'
            textArea.style.opacity = '0'
            document.body.appendChild(textArea)
            textArea.select()
            document.execCommand('copy')
            document.body.removeChild(textArea)
            setGenresCopied(true)
            setTimeout(() => setGenresCopied(false), 2000)
        })
    }

    const toggleShowAllKeywords = () => setShowAllKeywords(prev => !prev)

    const handleOpen = () => setShowModal(true)
    const handleClose = () => setShowModal(false)

    // Reset keyword tracking call tracker when country or language changes
    useEffect(() => {
        if (selectedCountry && selectedLanguage) {
            // Clear all tracking for this country/language pair
            Object.keys(keywordTrackingCallTracker.current).forEach(key => {
                if (key.includes(`_${selectedCountry}_${selectedLanguage}`)) {
                    keywordTrackingCallTracker.current[key] = false
                }
            })
        }
    }, [selectedCountry, selectedLanguage])
    const memoizedResults = useMemo(() => {
        return keywordTrackingData ? Object.values(keywordTrackingData) : []
    }, [keywordTrackingData])

    // Stable search handlers
    const handleSearchChange = useCallback(e => {
        setSearchInput(e.target.value)
    }, [])

    const handleSearchClick = useCallback(() => {
        fetchKeywordTracking(selectedCountry, selectedLanguage, fromDate, toDate)
    }, [
        fetchKeywordTracking,
        selectedCountry,
        selectedLanguage,
        fromDate,
        toDate,
    ])

    const handleSelectApp = useCallback(app => {
        console.log("Selected app:", app)
    }, [])

    const handleRemoveTrackedPair = async (pair, opts = {}) => {
        if (!pair) return

        // Parse country and language from "Country (Language)" if possible
        const match = pair.match(/(.*) \((.*)\)/)
        const country = match ? match[1] : pair
        const language = match ? match[2] : null

        // Determine if this pair has keywords (categories)
        const pairKey = language ? `${country}_${language}` : null
        const hasKeywords = pairKey
            ? (keywordsByCountryLanguage[pairKey] || []).length > 0
            : false

        // If the pair has keywords but caller didn't explicitly request deletion of keywords, abort.
        if (hasKeywords && !opts.deleteKeywords) {
            // Do nothing — require explicit confirmation to delete keywords along with the pair
            return
        }

        trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_GENRE_REMOVED, {
            country,
            language,
            scope: "pair",
        })

        // 0️⃣ Delete from backend if requested
        if (opts.deleteKeywords && hasKeywords && pairKey) {
            const keywordsToRemove = keywordsByCountryLanguage[pairKey] || []
            // Execute removals in background (Fire and Forget)
            Promise.all(keywordsToRemove.map(kw => removeEntity(kw, "category", { country, language })))
                .catch(e => console.error("Error removing categories for pair:", e))
        }

        // 1) Remove the pair from trackedCountryLanguagePairs and persist
        setTrackedCountryLanguagePairs(prev => {
            const updated = prev.filter(item => item !== pair)
            console.log("LOGGINGGGG HEREEEE 1791");

            storeValue(item_id, "tracked_country_language_pairs", updated)
            return updated
        })

        // 2) Remove keywords associated with this pair (if any) and persist
        setKeywordsByCountryLanguage(prev => {
            const updated = { ...prev }
            if (language) {
                const pKey = `${country}_${language}`
                if (pKey in updated) {
                    delete updated[pKey]
                }
            } else {
                // if no language, remove any keys for this country
                Object.keys(updated).forEach(k => {
                    if (k.startsWith(`${country}_`)) delete updated[k]
                })
            }
            storeValue(item_id, "keywords_by_country_language", updated)
            return updated
        })

        // 3) Only remove country from trackedCountries if no other pairs for that country remain
        const remainingPairsForCountry = trackedCountryLanguagePairs.filter(p => {
            if (p === pair) return false
            const m = p.match(/(.*) \((.*)\)/)
            return m && m[1] === country
        })
        if (remainingPairsForCountry.length === 0) {
            setTrackedCountries(prev => {
                const updated = prev.filter(c => c !== country)
                storeValue(item_id, "tracked_countries", updated)
                return updated
            })
        }

        // 4) Clear active tracked pair/country if it was the one removed
        if (activeTrackedPair === pair) {
            // Find the next best pair to select based on INDEX (keep user context)
            const currentIndex = trackedCountryLanguagePairs.indexOf(pair)

            const newPairs = trackedCountryLanguagePairs.filter(p => p !== pair)
            let nextPair = null

            if (newPairs.length > 0) {
                const nextIndex = currentIndex < newPairs.length ? currentIndex : newPairs.length - 1
                nextPair = newPairs[nextIndex]
            }

            if (nextPair) {
                setActiveTrackedPair(nextPair);
                storeValue(item_id, "active_tracked_pair", nextPair);

                const match = nextPair.match(/(.*) \((.*)\)/)
                if (match) {
                    const nextCountry = match[1]
                    const nextLanguage = match[2]
                    setSelectedCountry(nextCountry);
                    setSelectedLanguage(nextLanguage);
                    storeValue(item_id, "selected_country", nextCountry);
                    storeValue(item_id, "selected_language", nextLanguage);

                    // Also set active tracked country
                    setActiveTrackedCountry(nextCountry)
                    storeValue(item_id, "active_tracked_country", nextCountry)
                }

                // Force data fetch for the new selection
                // IMPORTANT: Reset trackers so the fetch actually happens!
                lastSelectedPairRef.current = null
                if (match) {
                    const callKey = `${match[1]}_${match[2]}`
                    if (apiCallTracker.current) {
                        apiCallTracker.current[callKey] = false
                    }
                }

                setInitialFetchDone(true)
            } else {
                // No pairs left — app has zero tracked genres, remove it from tracking
                await removeAppFromTracking()
                return
            }
        }
        if (activeTrackedCountry === country && trackedCountries.filter(c => c !== country).length === 0) {
            setActiveTrackedCountry("")
            storeValue(item_id, "active_tracked_country", "")
        }
    }

    // ✅ Handle File Import (CSV or TXT)
    const handleImportKeywords = event => {
        const file = event.target.files[0]
        if (!file) return

        const reader = new FileReader()
        reader.onload = e => {
            const content = e.target.result

            // Split by commas or new lines
            const keywords = content
                .split(/[\n,]+/)
                .map(k => k.trim())
                .filter(Boolean)

            if (keywords.length === 0) {
                alert("No keywords found in the file.")
                return
            }

            // Load into the keyword input for review
            setSearchInput(keywords.join(", "))

            // Optional: automatically add them
            // onAddKeyword()

            // Reset file input so user can re-upload the same file if needed
            event.target.value = ""
        }

        reader.readAsText(file)
    }

    // ✅ Handle Sample File Download
    const handleDownloadSample = () => {
        const sampleFilePath = "/samples/sample_keywords.txt" // stored in /public/samples/

        const link = document.createElement("a")
        link.href = sampleFilePath
        link.download = "sample_keywords.txt"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    // Early return for 404 Not Found (after all hooks)
    if (notFoundError && !keywordsLoading) {
        return (
            <>
                <SEO title="Page Not Found" description="The requested app could not be found." />
                <NotFound
                    message="The app you're looking for doesn't exist or has been removed."
                    linkText="Back to Dashboard"
                    linkTo="/"
                />
            </>
        )
    }

    return (
        <React.Fragment>
            <SEO title="Category Details" description="Detailed category tracking analysis." />
            <div className="keyword_track_detail">
                <div className="container-fluid p-4">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="keyword_track_detail_hed">
                                <h3 className="text-white">Genre Tracking</h3>
                            </div>

                            <AppHeader
                                trackedApp={trackedApp}
                                onRemoveApp={() => handleRemoveEntity(null)}
                            />

                            {/* Search Instructions */}
                            <div style={{ marginTop: '30px', marginBottom: '20px', textAlign: 'left' }}>
                                <p style={{
                                    color: '#9ca3af',
                                    fontSize: '14px',
                                    margin: '0',
                                    fontStyle: 'italic',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <i
                                        className="fas fa-info-circle"
                                        style={{
                                            fontSize: '14px',
                                            color: '#6b7280',
                                            fontStyle: 'normal'
                                        }}
                                    ></i>
                                    Start searching for genres to track their performance - you can adjust country and language settings anytime
                                </p>
                            </div>

                            <div className="select_grp_popup">
                                <KeywordTrackingDetailsModal
                                    show={showModal}
                                    handleClose={handleClose}
                                    title="My Modal"
                                    searchTerm={searchInput}
                                    onSearchChange={handleSearchChange}
                                    onSearchClick={handleSearchClick}
                                    results={memoizedResults} // ✅ stable
                                    loading={keywordsLoading}
                                    onSelectApp={handleSelectApp} // ✅ stable
                                    onUpgrade={() => {
                                        setShowUpgradeModal(true)
                                        setShowModal(false)
                                    }}
                                />
                            </div>

                            {/* Search Bar with Country/Language Dropdowns and Import Button */}
                            <div className="row align-items-center mb-3">
                                <div className="col-lg-7 col-md-6 col-sm-12">
                                    <KeywordSearch
                                        searchInput={searchInput}
                                        setSearchInput={setSearchInput}
                                        onAddKeyword={handleAddKeyword}
                                        addingKeyword={addingKeyword || isTrackingInProgress}
                                        selectedCountry={selectedCountry}
                                        selectedLanguage={selectedLanguage}
                                        keywordCount={Object.values(keywordsByCountryLanguage).reduce((t, ks) => t + (ks || []).filter(k => k !== 'game_data').length, 0)}
                                        keywordLimit={25}
                                        planLoading={false}
                                        onGenerateKeywords={() => setShowSuggestedGenresModal(true)}
                                        onLimitReached={() => setShowUpgradeModal(true)}
                                    />
                                </div>
                                <div className="col-lg-5 col-md-6 col-sm-12">
                                    <div className="d-flex gap-2 align-items-center justify-content-end justify-content-sm-start mt-sm-2 mt-md-0 flex-wrap" style={{ paddingTop: windowWidth < 768 ? '16px' : '0' }}>
                                        {/* Import Genres Button - Desktop Only */}
                                        {windowWidth >= 768 && (
                                            <button
                                                onClick={() => {
                                                    document.getElementById('keywordFileUpload').click()
                                                }}
                                                style={{
                                                    backgroundColor: !selectedCountry || !selectedLanguage ? '#4b5563' : COLORS.primary,
                                                    color: COLORS.white,
                                                    padding: "10px",
                                                    borderRadius: "4px",
                                                    border: "none",
                                                    cursor: !selectedCountry || !selectedLanguage ? 'not-allowed' : 'pointer',
                                                    fontSize: "14px",
                                                    width: "40px",
                                                    height: "40px",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    opacity: !selectedCountry || !selectedLanguage ? 0.6 : 1,
                                                    transition: "all 0.2s ease",
                                                    flexShrink: 0
                                                }}
                                                title="Import Genres"
                                                disabled={!selectedCountry || !selectedLanguage}
                                            >
                                                <i
                                                    className="fas fa-upload"
                                                    style={{ fontSize: "12px" }}
                                                ></i>
                                            </button>
                                        )}

                                        {/* Country — shared with the language picker below and with
                                            KeywordTracking's identical block via FlagSelectDropdown,
                                            so the two controls can't drift out of visual sync again. */}
                                        <FlagSelectDropdown
                                            options={countriesData.map(country => country.name)}
                                            value={selectedCountry}
                                            onChange={handleCountryChange}
                                            placeholder="Select Country"
                                            disabled={isTrackingInProgress}
                                            {...KEYWORD_TRACKING_THEME}
                                            minWidth={140}
                                            maxWidth={200}
                                            icon={(countryName) => {
                                                const FlagComponent = getCountryFlag(countryName)
                                                return FlagComponent ? <FlagComponent style={{ width: '16px', height: 'auto' }} /> : null
                                            }}
                                        />

                                        {/* Language */}
                                        <FlagSelectDropdown
                                            options={availableLanguages}
                                            value={selectedLanguage}
                                            onChange={handleLanguageChange}
                                            placeholder="Select Language"
                                            disabled={!selectedCountry || availableLanguages.length === 0 || isTrackingInProgress}
                                            {...KEYWORD_TRACKING_THEME}
                                            minWidth={120}
                                            maxWidth={180}
                                            noOptionsText="Select a country first"
                                        />

                                    </div>
                                </div>
                            </div>

                            {/* Hidden file input for import functionality */}
                            <input
                                id="keywordFileUpload"
                                type="file"
                                accept=".csv,.txt"
                                style={{ display: "none" }}
                                onChange={handleImportKeywords}
                                disabled={!selectedCountry || !selectedLanguage}
                            />

                            {/* Tracked Data Availability Section - Full Width */}
                            {trackedCountryLanguagePairs.length > 0 && (
                                <div style={{
                                    background: '#1a1b23',
                                    borderRadius: '8px',
                                    padding: '20px',
                                    marginBottom: '20px',
                                    border: '1px solid #3A3B47'
                                }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "12px",
                                        }}
                                    >
                                        <label style={{
                                            color: COLORS.white,
                                            fontSize: "16px",
                                            fontWeight: 600
                                        }}>
                                            Tracked Data Availability
                                        </label>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '8px',
                                        marginBottom: '12px'
                                    }}>
                                        {trackedCountryLanguagePairs.map((pair, index) => {
                                            let isActive = false;

                                            if (activeTrackedPair) {
                                                isActive = pair === activeTrackedPair;
                                            } else if (activeTrackedCountry) {
                                                isActive = pair.startsWith(activeTrackedCountry);
                                            } else if (selectedCountry && selectedLanguage) {
                                                const currentSelection = `${selectedCountry} (${selectedLanguage})`;
                                                isActive = pair === currentSelection || pair === selectedCountry;
                                            }

                                            const match = pair.match(/(.*) \((.*)\)/)
                                            const pairKey = match ? `${match[1]}_${match[2]}` : ''
                                            const allKeywords = (keywordsByCountryLanguage[pairKey] || []).filter(k => k !== "game_data")
                                            const isPairPending = pendingPairs.has(pair)

                                            return (
                                                <div
                                                    key={`${pair}-${index}`}
                                                    style={{
                                                        background: isActive ? COLORS.primary : '#2A2B37',
                                                        color: isPairPending ? '#6b7280' : COLORS.white,
                                                        padding: '6px 10px',
                                                        borderRadius: '6px',
                                                        fontSize: '0.9rem',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s ease',
                                                        border: '1px solid #3A3B47',
                                                        animation: isPairPending ? 'pulse 1.5s ease-in-out infinite' : 'none',
                                                    }}
                                                >
                                                    <div
                                                        style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                                        onClick={() => handleTrackedPairClick(pair)}
                                                    >
                                                        {/* Country Flag */}
                                                        {(() => {
                                                            const actualCountry = pair.includes("(") ? pair.split("(")[0].trim() : pair;
                                                            const countryCode = getCountryCode(actualCountry)
                                                            const FlagComponent = flags[countryCode]

                                                            return FlagComponent ? (
                                                                <FlagComponent
                                                                    style={{
                                                                        width: '16px',
                                                                        height: 'auto',
                                                                        marginRight: '6px',
                                                                        verticalAlign: 'middle',
                                                                    }}
                                                                />
                                                            ) : null;
                                                        })()}
                                                        <span>{pair}</span>
                                                    </div>

                                                    {/* Keyword Count Badge / Pending indicator */}
                                                    {isPairPending ? (
                                                        <span style={{
                                                            color: '#6b7280',
                                                            fontSize: '0.7rem',
                                                            fontStyle: 'italic',
                                                            marginLeft: '4px'
                                                        }}>
                                                            adding…
                                                        </span>
                                                    ) : !categoryTrackingDataLoaded && allKeywords.length === 0 ? (
                                                        // Genuinely zero keywords and "not fetched yet" both look like an
                                                        // empty array here — on a fresh browser session (no localStorage
                                                        // cache) that used to render nothing at all, which read as the
                                                        // count being missing. Show a loading badge instead until the
                                                        // mount-time fetch resolves, then it'll settle on the real count.
                                                        <span style={{
                                                            color: '#6b7280',
                                                            fontSize: '0.7rem',
                                                            fontStyle: 'italic',
                                                            marginLeft: '4px'
                                                        }}>
                                                            loading…
                                                        </span>
                                                    ) : (
                                                        <span style={{
                                                            background: isActive ? '#2A2B37' : COLORS.primary,
                                                            color: COLORS.white,
                                                            padding: '2px 6px',
                                                            borderRadius: '10px',
                                                            fontSize: '0.7rem',
                                                            fontWeight: 600,
                                                            marginLeft: '4px'
                                                        }}>
                                                            {allKeywords.length}
                                                        </span>
                                                    )}

                                                    {/* Remove button */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPairToDelete(pair);
                                                        }}
                                                        style={{
                                                            background: 'transparent',
                                                            border: 'none',
                                                            color: COLORS.white,
                                                            cursor: 'pointer',
                                                            padding: '2px',
                                                            borderRadius: '2px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            opacity: 0.7,
                                                            '&:hover': {
                                                                opacity: 1,
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                            }
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.opacity = '1';
                                                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.opacity = '0.7';
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }}
                                                        title="Remove country"
                                                    >
                                                        <i className="fas fa-times" style={{ fontSize: '12px' }}></i>
                                                    </button>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Tracked Keywords Section - Full Width */}
                            {getKeywordsForActivePair().length > 0 && (
                                <div style={{
                                    background: '#1a1b23',
                                    borderRadius: '8px',
                                    padding: '20px',
                                    marginBottom: '20px',
                                    border: '1px solid #3A3B47'
                                }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "12px",
                                        }}
                                    >
                                        <label
                                            style={{
                                                fontWeight: 600,
                                                color: COLORS.white,
                                                fontSize: "16px",
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            Tracked Genres ({getKeywordsForActivePair().length})
                                            {pendingKeywords.size > 0 && (
                                                <span style={{
                                                    fontSize: '12px',
                                                    color: '#9ca3af',
                                                    fontWeight: 400,
                                                }}>
                                                    adding…
                                                </span>
                                            )}
                                        </label>
                                        <button
                                            onClick={handleCopyGenres}
                                            title="Copy all genres"
                                            style={{
                                                background: genresCopied ? '#1a3a2a' : '#2A2B37',
                                                border: `1px solid ${genresCopied ? '#22c55e' : '#3A3B47'}`,
                                                color: genresCopied ? '#22c55e' : '#9ca3af',
                                                borderRadius: '6px',
                                                padding: '5px 10px',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                transition: 'all 0.2s ease',
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className={genresCopied ? 'fas fa-check' : 'fas fa-copy'} style={{ fontSize: '12px' }}></i>
                                            <span style={{ display: 'inline' }}>{genresCopied ? 'Copied!' : 'Copy'}</span>
                                        </button>
                                    </div>

                                    <div>
                                        <div style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '8px',
                                            marginBottom: '12px'
                                        }}>
                                            {(showAllKeywords
                                                ? getKeywordsForActivePair()
                                                : getKeywordsForActivePair().slice(0, 6)
                                            ).map((keyword, index) => {
                                                const fullIndex = getKeywordsForActivePair().indexOf(keyword)
                                                const isPending = pendingKeywords.has(keyword)
                                                return (
                                                    <span
                                                        key={keyword}
                                                        className="keyword-tag"
                                                        draggable={!isPending}
                                                        onDragStart={e => {
                                                            e.dataTransfer.setData("text/plain", fullIndex.toString())
                                                        }}
                                                        onDragOver={e => e.preventDefault()}
                                                        onDrop={e => {
                                                            const draggedIndex = Number(e.dataTransfer.getData("text/plain"))
                                                            const droppedIndex = fullIndex
                                                            if (draggedIndex === droppedIndex) return

                                                            const pairKey = getActivePairKey()
                                                            setKeywordsByCountryLanguage(prev => {
                                                                const keywords = [...(prev[pairKey] || [])]
                                                                const [movedKeyword] = keywords.splice(draggedIndex, 1)
                                                                keywords.splice(droppedIndex, 0, movedKeyword)

                                                                // Save updated keywords immediately
                                                                storeValue(
                                                                    item_id,
                                                                    "keywords_by_country_language",
                                                                    {
                                                                        ...prev,
                                                                        [pairKey]: keywords,
                                                                    }
                                                                )

                                                                return {
                                                                    ...prev,
                                                                    [pairKey]: keywords,
                                                                }
                                                            })
                                                        }}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            background: '#2A2B37',
                                                            color: isPending ? '#6b7280' : COLORS.white,
                                                            padding: '6px 12px',
                                                            borderRadius: '16px',
                                                            fontSize: '0.85rem',
                                                            border: '1px solid #3A3B47',
                                                            transition: 'all 0.2s ease',
                                                            animation: isPending ? 'pulse 1.5s ease-in-out infinite' : 'none',
                                                        }}
                                                    >
                                                        {keyword}
                                                        {!isPending && (
                                                            <button
                                                                onClick={e => {
                                                                    e.preventDefault()
                                                                    setKeywordToDelete(keyword)
                                                                }}
                                                                style={{
                                                                    marginLeft: "6px",
                                                                    border: "none",
                                                                    background: "transparent",
                                                                    cursor: "pointer",
                                                                    fontSize: "0.8rem",
                                                                    color: "#888",
                                                                }}
                                                                aria-label={`Remove keyword ${keyword}`}
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </span>
                                                )
                                            })}
                                        </div>

                                        {getKeywordsForActivePair().length > 6 && (
                                            <button
                                                onClick={() => setShowAllKeywords(prev => !prev)}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: COLORS.primary,
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem',
                                                    padding: '4px 0'
                                                }}
                                            >
                                                {showAllKeywords
                                                    ? "Show Less"
                                                    : `Show More (${getKeywordsForActivePair().length - 6})`}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* No Keywords Message */}
                            {getKeywordsForActivePair().length === 0 && selectedCountry && selectedLanguage && (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '20px',
                                    color: '#94a3b8',
                                    fontSize: '14px',
                                    fontStyle: 'italic',
                                    background: '#1a1b23',
                                    borderRadius: '8px',
                                    border: '1px solid #3A3B47',
                                    marginBottom: '20px'
                                }}>
                                    No tracked genres for this country and language. Add genres above to start tracking.
                                </div>
                            )}

                            <Modal
                                isOpen={!!keywordToDelete}
                                toggle={() => setKeywordToDelete(null)}
                                centered
                            >
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirm Deletion</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setKeywordToDelete(null)}
                                        aria-label="Close"
                                    />
                                </div>
                                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                                    <p>
                                        Are you sure you want to remove "<b>{keywordToDelete}</b>"
                                        keyword?
                                        <br /> Data for this keyword will be removed.
                                    </p>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            gap: "10px",
                                        }}
                                    >
                                        <Button
                                            color="danger"
                                            onClick={() => {
                                                handleRemoveEntity(keywordToDelete) // actually remove
                                                setKeywordToDelete(null) // close modal
                                            }}
                                        >
                                            Yes
                                        </Button>
                                        <Button
                                            color="secondary"
                                            onClick={() => setKeywordToDelete(null)}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </ModalBody>
                            </Modal>

                            {/* Pair Deletion Confirmation Modal */}
                            <Modal
                                isOpen={!!pairToDelete}
                                toggle={() => setPairToDelete(null)}
                                centered
                            >
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirm Pair Deletion</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setPairToDelete(null)}
                                        aria-label="Close"
                                    />
                                </div>
                                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                                    <p>
                                        Are you sure you want to remove "<b>{pairToDelete}</b>"
                                        and all its tracked categories?
                                        <br /> All data for this country/language pair will be permanently removed.
                                    </p>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            gap: "10px",
                                        }}
                                    >
                                        <Button
                                            color="danger"
                                            onClick={() => {
                                                handleRemoveTrackedPair(pairToDelete, { deleteKeywords: true }) // actually remove
                                                setPairToDelete(null) // close modal
                                            }}
                                        >
                                            Yes
                                        </Button>
                                        <Button
                                            color="secondary"
                                            onClick={() => setPairToDelete(null)}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </ModalBody>
                            </Modal>

                            {/* Add CSS for improved styling */}
                            <style jsx>{`
                .tracked-keywords-container {
                  display: flex;
                  flex-direction: column;
                  gap: 10px;
                  margin-top: 0;
                }
                .tracked-keywords-list {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 8px;
                  max-width: 100%;
                }
                .keyword-tag {
                  display: inline-flex;
                  align-items: center;
                  background: #f0f0f0;
                  color: #333;
                  padding: 4px 10px;
                  border-radius: 16px;
                  font-size: 0.9rem;
                  text-decoration: none;
                  transition: background-color 0.2s;
                  margin-bottom: 5px;
                }
                .keyword-tag:hover {
                  background: #f6255a;
                  color: white;
                }
                .keyword-tag i {
                  margin-left: 6px;
                  font-size: 0.8rem;
                }
                .show-more-less {
                  color: #f6255a;
                  font-weight: bold;
                  text-decoration: none;
                  align-self: flex-start;
                }
              `}</style>
                        </div>

                        <div className="track_list">
                            <DateRangeSelector
                                fromDate={fromDate}
                                toDate={toDate}
                                setFromDate={setFromDate}
                                setToDate={setToDate}
                            />

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    marginBottom: "6px",
                                }}
                            >

                                {/* Export — Left */}
                                <div className="csv_btn">
                                    <a
                                        href="#"
                                        onClick={e => {
                                            e.preventDefault()
                                            setShowExportModal(true)
                                        }}
                                    >
                                        Export
                                    </a>
                                </div>

                                {/* Last Updated — Right */}
                                <div
                                    className="update_timestamp"
                                    style={{
                                        fontSize: "12px",
                                        fontStyle: "italic",
                                        color: "#94a3b8",
                                        whiteSpace: "nowrap",
                                        marginTop: "48px",
                                    }}
                                >
                                    {trackedApp?.update_time != null
                                        ? `Last updated: ${new Date(trackedApp.update_time * 1000).toLocaleString()}`
                                        : "Last updated: N/A"}
                                </div>

                            </div>

                            {/* {trackedApp?.update_time != null ? (
                                    <div
                                        className="update_timestamp"
                                        style={{ marginTop: "8px", marginBottom: "12px" }}
                                    >
                                        <span style={{ fontWeight: "500" }}>Last Updated:</span>{" "}
                                        {new Date(trackedApp.update_time * 1000).toLocaleString()}
                                    </div>
                                ) : (
                                    <div
                                        className="update_timestamp"
                                        style={{
                                            marginTop: "8px",
                                            marginBottom: "12px",
                                            color: "#888",
                                        }}
                                    >
                                        Last Updated: N/A
                                    </div>
                                )}

                                <div className="csv_btn">
                                    <a
                                        href="#"
                                        onClick={e => {
                                            e.preventDefault()
                                            setShowExportModal(true)
                                        }}
                                    >
                                        Export
                                    </a>
                                </div> */}

                            <KeywordTrackingTable
                                keywordsLoading={keywordsLoading}
                                keywordTrackingData={keywordTrackingData}
                                item_id={item_id}
                                countryName={selectedCountry}
                                // A newly tracked app has no country/language yet, so
                                // nothing will load until the user picks one - show a
                                // prompt instead of an endless skeleton. Apps with a
                                // tracked pair get it auto-selected, so they keep the
                                // skeleton while that loads.
                                awaitingLocaleSelection={
                                    (!selectedCountry || !selectedLanguage) &&
                                    summaryLoaded &&
                                    trackedCountryLanguagePairs.length === 0
                                }
                            />
                        </div>
                    </div>
                    {/* Export Modal */}
                    <ExportModal
                        show={showExportModal}
                        handleClose={() => setShowExportModal(false)}
                        gameId={item_id} // 👈 required
                        dataType="keyword" // 👈 required
                    />
                    <AppsTrackingPlanModal
                        show={showUpgradeModal}
                        handleClose={() => setShowUpgradeModal(false)}
                        title=" Upgrade your plan to unlock this feature"
                    />
                    <UnsavedChangesModal
                        show={showUnsavedChangesModal}
                        onConfirm={confirmLeaveWithoutSaving}
                        onCancel={cancelLeaveWithoutSaving}
                        loading={isNavigating}
                    />
                    <SuggestedGenresModal
                        isOpen={showSuggestedGenresModal}
                        toggle={() => setShowSuggestedGenresModal(!showSuggestedGenresModal)}
                        itemId={item_id}
                        selectedCountry={selectedCountry}
                        selectedLanguage={selectedLanguage}
                        trackedGenres={getKeywordsForCurrentPair()}
                        onAddGenres={selectedGenres => {
                            if (selectedGenres.length > 0) {
                                handleAddKeyword(selectedGenres.join(','))
                            }
                        }}
                        appMetadata={appMetadata}
                        keywordCount={Object.values(keywordsByCountryLanguage).reduce((t, ks) => t + (ks || []).filter(k => k !== 'game_data').length, 0)}
                        keywordCountLoading={!categoryTrackingDataLoaded}
                        keywordLimit={getKeywordLimit()}
                        onUpgrade={() => { setShowSuggestedGenresModal(false); setShowUpgradeModal(true); }}
                    />
                </div>
            </div>
        </React.Fragment>
    )
}

export default KeywordDetails
